/**
 * <asd-restock-notify> shows a "Notify me when it's back" button while the selected variant is sold out,
 * and a popup where the customer leaves their email (Shopify customer form) or texts a Postscript keyword.
 *
 * Markup comes from blocks/restock-notify.liquid:
 *   - data-variants is { variantId: { a: available, t: title } } for every variant,
 *   - [data-rn-tags] is the hidden contact[tags] input; its value follows the selected variant,
 *   - data-form-id / data-posted / data-errors let it reopen the popup after the form posts back.
 */
class AsdRestockNotify extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {Record<string, { a: boolean, t: string }>} */
  #variants = {};
  #lastVariantId = '';
  /** @type {number | undefined} */
  #pollTimer;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-rn-dialog]');

    try {
      this.#variants = JSON.parse(this.dataset.variants ?? '{}');
    } catch {
      this.#variants = {};
    }

    this.querySelector('[data-rn-open]')?.addEventListener('click', this.#open);
    this.querySelector('[data-rn-close]')?.addEventListener('click', this.#close);

    const dialog = this.#dialog;
    if (dialog) {
      // Clicking the backdrop (the dialog element itself) closes it.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.#close();
      });
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
      });
    }

    document.addEventListener('shopify:product:select', this.#onVariantSelect);
    this.#update();
    this.#restoreAfterPost();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:product:select', this.#onVariantSelect);
    window.clearInterval(this.#pollTimer);
  }

  /** The variant picker fetches the new variant before the form's id input changes, so watch it for a few seconds. */
  #onVariantSelect = () => {
    window.clearInterval(this.#pollTimer);
    const startedWith = this.#currentVariantId();
    let ticks = 0;
    this.#pollTimer = window.setInterval(() => {
      ticks += 1;
      if (this.#currentVariantId() !== startedWith || ticks > 30) {
        window.clearInterval(this.#pollTimer);
        this.#update();
      }
    }, 100);
  };

  /** Show the button only for a sold-out variant, and point the signup at that variant. */
  #update() {
    const variantId = this.#currentVariantId();
    if (variantId === this.#lastVariantId) return;
    this.#lastVariantId = variantId;

    const variant = this.#variants[variantId];
    const soldOut = variant ? !variant.a : false;
    this.hidden = !(soldOut || this.hasAttribute('data-design-mode'));

    const sizeLabel = variant && variant.t !== 'Default Title' ? variant.t : '';
    for (const el of this.querySelectorAll('[data-rn-variant]')) {
      el.textContent = sizeLabel;
      if (el instanceof HTMLElement) el.hidden = !sizeLabel;
    }

    const tags = this.querySelector('[data-rn-tags]');
    if (tags instanceof HTMLInputElement) {
      const base = this.dataset.baseTags ?? '';
      const variantTag = sizeLabel ? `, ${this.dataset.productTag}-${this.#handleize(sizeLabel)}` : '';
      tags.value = base + variantTag;
    }
  }

  /** After the email form posts, Shopify reloads the page with #<form id>; reopen the popup to show the result. */
  #restoreAfterPost() {
    const ownForm = window.location.hash === `#${this.dataset.formId}`;
    const posted = ownForm && this.dataset.posted === 'true';
    const failed = ownForm && this.dataset.errors === 'true';

    this.querySelector('[data-rn-success]')?.toggleAttribute('hidden', !posted);
    this.querySelector('[data-rn-error]')?.toggleAttribute('hidden', !failed);
    this.querySelector('[data-rn-form-body]')?.toggleAttribute('hidden', posted);

    if (ownForm && (posted || failed)) {
      this.hidden = false;
      this.#open();
    }
  }

  #open = () => {
    if (typeof this.#dialog?.showModal === 'function' && !this.#dialog.open) {
      this.#dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
  };

  #close = () => {
    this.#dialog?.close();
  };

  /** The variant currently selected on the product page, or '' if it can't be found. */
  #currentVariantId() {
    const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input instanceof HTMLInputElement && input.value) return input.value;
    return new URLSearchParams(window.location.search).get('variant') ?? this.dataset.initialVariant ?? '';
  }

  /** @param {string} text */
  #handleize(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

if (!customElements.get('asd-restock-notify')) {
  customElements.define('asd-restock-notify', AsdRestockNotify);
}
